/**
 * The only file that names `@stripe/stripe-js` (TMMAPP-0015). Memoizes `loadStripe` per
 * `(publishableKey, stripeAccount)` so React StrictMode double-invokes stay safe.
 *
 * Bank data (Stripe Financial Connections, TMMAPP-0052) lives in a separate Stripe account: its
 * instance is created with the `link-session` response's key, memoized per key apart from the
 * payments memo, and never replaces or reconfigures the payments instance.
 */
import {
  loadStripe,
  type Appearance,
  type FinancialConnectionsSession,
  type Stripe,
  type StripeElementLocale,
  type StripeElementStyle,
} from '@stripe/stripe-js';

export type StripeClient = Stripe;
export type FinancialConnectionsAccount = FinancialConnectionsSession.Account;

/** The three split card fields (TMMAPP-0119 item 22). */
export type CardField = 'number' | 'expiry' | 'cvc';

/** Where each Element mounts: the three card fields, then the billing address. */
export type CardElementTargets = Record<CardField | 'address', HTMLElement>;

export interface CardElementsOptions {
  /** The Address Element's appearance (the Elements appearance already in use). */
  appearance: Appearance;
  /** The split card fields' text style: the body face and the text tokens. */
  style: StripeElementStyle;
  /** The Address Element's default country (ISO 3166-1 alpha-2), or null for Stripe's own. */
  country: string | null;
  /** The app's language (`i18n.language`): Stripe's fields read in it (TMMAPP-0122 item 26). */
  locale: string;
  /** A card field's state changed: complete, and the Element's validation code (never its text). */
  onFieldChange: (field: CardField, complete: boolean, errorCode: string | null) => void;
  /** A card field gained or lost focus (the outlined box draws the ring). */
  onFieldFocus: (field: CardField, focused: boolean) => void;
  /** The Address Element changed: an address refusal shown under the card clears (item 27). */
  onAddressChange: () => void;
}

/** The app's languages Stripe's Elements also speak; any other reads as Stripe's `auto`. */
const ELEMENT_LOCALES: ReadonlySet<string> = new Set(['en', 'es', 'fr', 'pt']);

/** An app language (`pt-BR`, `fr`) as an Elements locale: its base language, else `auto`. */
export function elementsLocale(language: string): StripeElementLocale {
  const base = language.split('-')[0]?.toLowerCase() ?? '';
  return ELEMENT_LOCALES.has(base) ? (base as StripeElementLocale) : 'auto';
}

export type CardTokenizeResult =
  | { ok: true; paymentMethodId: string }
  /** The Address Element isn't complete: it shows its own errors, and nothing is tokenized. */
  | { ok: false; reason: 'address' }
  /** Stripe refused: its error `code` only, never its message text. */
  | { ok: false; reason: 'card'; code: string | null };

/** The mounted card capture. Card data and the address stay inside Stripe's Elements. */
export interface CardElements {
  tokenize: () => Promise<CardTokenizeResult>;
  destroy: () => void;
}

/**
 * The card and its billing address (TMMAPP-0119 §B, item 22): the split `cardNumber` / `cardExpiry`
 * / `cardCvc` Elements (no postal code: the address carries it) and the Address Element in
 * `mode: 'billing'`. Tokenize validates the address (the Element shows its own errors), then
 * creates the PaymentMethod from the number Element with the address as its `billing_details`.
 * The address goes to Stripe only: it never leaves this function.
 */
export function mountCardElements(
  stripe: Stripe,
  targets: CardElementTargets,
  options: CardElementsOptions,
): CardElements {
  const elements = stripe.elements({
    appearance: options.appearance,
    locale: elementsLocale(options.locale),
  });
  const number = elements.create('cardNumber', { style: options.style });
  const expiry = elements.create('cardExpiry', { style: options.style });
  const cvc = elements.create('cardCvc', { style: options.style });
  const address = elements.create('address', {
    mode: 'billing',
    ...(options.country !== null
      ? { defaultValues: { address: { country: options.country } } }
      : {}),
  });
  const fields: ReadonlyArray<readonly [CardField, typeof number | typeof expiry | typeof cvc]> = [
    ['number', number],
    ['expiry', expiry],
    ['cvc', cvc],
  ];
  for (const [field, element] of fields) {
    // The overloads are per element type; each of these emits the same base change event.
    const on = element.on.bind(element) as (
      event: string,
      handler: (event: { complete?: boolean; error?: { code?: string } }) => void,
    ) => void;
    on('change', (event) =>
      options.onFieldChange(field, event.complete === true, event.error?.code ?? null),
    );
    on('focus', () => options.onFieldFocus(field, true));
    on('blur', () => options.onFieldFocus(field, false));
    element.mount(targets[field]);
  }
  address.on('change', () => options.onAddressChange());
  address.mount(targets.address);

  return {
    async tokenize() {
      const { complete, value } = await address.getValue();
      if (!complete) return { ok: false, reason: 'address' };
      const { line1, line2, city, state, postal_code, country } = value.address;
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: number,
        billing_details: {
          name: value.name,
          address: {
            line1,
            city,
            postal_code,
            country,
            ...(line2 ? { line2 } : {}),
            ...(state ? { state } : {}),
          },
        },
      });
      if (result.error) {
        return { ok: false, reason: 'card', code: result.error.code ?? null };
      }
      const id = result.paymentMethod?.id;
      return typeof id === 'string' && id.length > 0
        ? { ok: true, paymentMethodId: id }
        : { ok: false, reason: 'card', code: null };
    },
    destroy() {
      for (const [, element] of fields) {
        element.unmount();
        element.destroy();
      }
      address.unmount();
      address.destroy();
    },
  };
}

let cached: { key: string; account: string | null; promise: Promise<Stripe | null> } | null = null;
const bankData = new Map<string, Promise<Stripe | null>>();

/** Clears the payments memo only; the bank-data instances are untouched. */
export function clearStripeMemo(): void {
  cached = null;
}

/** Test seam: clears the bank-data memo. */
export function clearBankDataStripeMemo(): void {
  bankData.clear();
}

export function getStripe(
  publishableKey: string,
  stripeAccount: string | null,
): Promise<Stripe | null> {
  if (cached && cached.key === publishableKey && cached.account === stripeAccount) {
    return cached.promise;
  }
  const promise = loadStripe(publishableKey, stripeAccount ? { stripeAccount } : undefined);
  cached = { key: publishableKey, account: stripeAccount, promise };
  return promise;
}

/**
 * The bank-data Stripe instance for `publishableKey` — always the `link-session` key, never the
 * billing client-config key.
 */
export function getBankDataStripe(publishableKey: string): Promise<Stripe | null> {
  const existing = bankData.get(publishableKey);
  if (existing) return existing;
  const promise = loadStripe(publishableKey);
  bankData.set(publishableKey, promise);
  // A failed load is not memoized, so Retry loads again.
  promise.catch(() => bankData.delete(publishableKey));
  return promise;
}
